import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { IconComponent } from '../icons';
import { ChevronRightIcon } from '../icons';
import './ActionCard.css';

export interface ActionCardProps {
  icon?: IconComponent;
  title: ReactNode;
  body?: ReactNode;
  /** Navigate on click. */
  to?: string;
  /** Or run something. */
  onClick?: () => void;
  /** Inline content (a FileDrop, a QrCard) instead of being clickable. */
  children?: ReactNode;
  /**
   * With `onToggle`: whether `children` are showing. The head becomes the
   * toggle, so a card with something bulky inside (a QR code) keeps the same
   * one-line shape as the cards that navigate until the user asks for it.
   */
  expanded?: boolean;
  onToggle?: () => void;
  className?: string;
}

/** Card that is an action: click target, or a container for an inline control. */
export function ActionCard({ icon: Icon, title, body, to, onClick, children, expanded, onToggle, className }: ActionCardProps) {
  const head = (
    <>
      {Icon ? (
        <span className="action-card-icon">
          <Icon size={22} />
        </span>
      ) : null}
      <span className="action-card-text">
        <span className="action-card-title">{title}</span>
        {body ? <span className="action-card-body">{body}</span> : null}
      </span>
      {to || onClick || onToggle ? (
        <ChevronRightIcon size={18} className={`action-card-chevron${expanded ? ' action-card-chevron-open' : ''}`} />
      ) : null}
    </>
  );
  const cls = ['action-card', className ?? ''].filter(Boolean).join(' ');
  if (to) {
    return (
      <Link to={to} className={`${cls} action-card-clickable`}>
        {head}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${cls} action-card-clickable`}>
        {head}
      </button>
    );
  }
  if (onToggle) {
    return (
      <div className={`${cls} action-card-expandable${expanded ? ' is-expanded' : ''}`}>
        <button type="button" className="action-card-head action-card-toggle" aria-expanded={expanded ?? false} onClick={onToggle}>
          {head}
        </button>
        {expanded && children ? <div className="action-card-content">{children}</div> : null}
      </div>
    );
  }
  return (
    <div className={cls}>
      <div className="action-card-head">{head}</div>
      {children ? <div className="action-card-content">{children}</div> : null}
    </div>
  );
}

/** Grid for 2–3 action cards. */
export function ActionCards({ children }: { children: ReactNode }) {
  return <div className="action-cards">{children}</div>;
}
